// Grids & Gantts — frontend API surface.

import { client, API_BASE, getToken } from '@platform/http/api-client';

const gridsApi = {
  // Grids visible to the current user → { grids: [{ slug, name, ... }] }
  async gridsList() {
    return (await client.get('/api/grids')).data;
  },
  // One grid, config included (403 server-side for non-members).
  async gridsGet(slug) {
    return (await client.get(`/api/grids/${encodeURIComponent(slug)}`)).data;
  },
  // Admin create: { name, description?, config?, restrictedGroups? }.
  // Refused (409) when the slug is taken.
  async gridsCreate(slug, payload) {
    return (await client.put(`/api/grids/${encodeURIComponent(slug)}`, payload)).data;
  },
  // Admin rename: { name, description? }. Config and access untouched.
  async gridsRename(slug, payload) {
    return (await client.patch(`/api/grids/${encodeURIComponent(slug)}`, payload)).data;
  },
  // Admin access: { restrictedGroups: [group names] } — empty = every
  // signed-in user. Nav buttons that link to the grid are synced server-side.
  async gridsSetAccess(slug, restrictedGroups) {
    return (await client.put(`/api/grids/${encodeURIComponent(slug)}/access`, { restrictedGroups })).data;
  },
  // The structure's own save: a patch of config keys ({ structure,
  // structureHistory }) merged over the grid's config.
  async gridsPatchConfig(slug, patch) {
    return (await client.put(`/api/grids/${encodeURIComponent(slug)}/config`, { patch })).data;
  },
  // A whole structure in one request, streamed: { structure, fields } →
  // progress lines while the server walks, then the compact result
  // (frontend/structure/compact.js). Resolves to the result with the
  // bytes received counted on it; onProgress sees { rows, phase }.
  async gridsBuild(payload, { onProgress, signal } = {}) {
    const token = getToken();
    const res = await fetch(`${API_BASE}/api/grids/build`, {
      method: 'POST', signal, credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { const j = await res.json(); msg = j.details || j.error || msg; } catch { /* not json */ }
      const err = new Error(msg); err.status = res.status; throw err;
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = ''; let bytes = 0; let result = null;
    const take = (line) => {
      const msg = JSON.parse(line);
      if (msg.progress && onProgress) onProgress(msg.progress);
      if (msg.error) throw new Error(msg.error);
      if (msg.result) result = msg.result;
    };
    for (;;) {
      /* eslint-disable no-await-in-loop */
      const { value, done } = await reader.read();
      /* eslint-enable no-await-in-loop */
      if (done) break;
      bytes += value.byteLength;
      buf += dec.decode(value, { stream: true });
      let i;
      while ((i = buf.indexOf('\n')) >= 0) { const line = buf.slice(0, i).trim(); buf = buf.slice(i + 1); if (line) take(line); }
    }
    if (buf.trim()) take(buf.trim());
    if (!result) throw new Error('The server ended the build without a result');
    result.wireBytes = bytes;
    return result;
  },
  // THE RESCHEDULER (structure/reschedule.js). What a whole move of one bar
  // would take along: { key, startFieldId, folderId? } → { levels, perLevel,
  // counts, items }. Read only.
  async gridsReschedulePlan(slug, payload) {
    return (await client.post(`/api/grids/${encodeURIComponent(slug)}/reschedule/plan`, payload || {})).data;
  },
  // The move itself, streamed: { key, startFieldId, folderId?, from, to } →
  // onPlan({ moving, staying, … }) as soon as the bar's own save landed,
  // onProgress({ done, total, key, ok }) per work item, then the result.
  // A refusal (403, 409 …) rejects with the server's words.
  async gridsReschedule(slug, payload, { onPlan, onProgress, signal } = {}) {
    const token = getToken();
    const res = await fetch(`${API_BASE}/api/grids/${encodeURIComponent(slug)}/reschedule`, {
      method: 'POST', signal, credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { const j = await res.json(); msg = j.error || j.details || msg; } catch { /* not json */ }
      const err = new Error(msg); err.status = res.status; throw err;
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = ''; let result = null; let failure = null;
    const take = (line) => {
      const msg = JSON.parse(line);
      if (msg.plan && onPlan) onPlan(msg.plan);
      if (msg.progress && onProgress) onProgress(msg.progress);
      if (msg.error) failure = msg.error;
      if (msg.result) result = msg.result;
    };
    for (;;) {
      /* eslint-disable no-await-in-loop */
      const { value, done } = await reader.read();
      /* eslint-enable no-await-in-loop */
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let i;
      while ((i = buf.indexOf('\n')) >= 0) { const line = buf.slice(0, i).trim(); buf = buf.slice(i + 1); if (line) take(line); }
    }
    if (buf.trim()) take(buf.trim());
    if (failure) throw new Error(failure);
    if (!result) throw new Error('The server ended the reschedule without a result');
    return result;
  },
  async gridsDelete(slug) {
    return (await client.delete(`/api/grids/${encodeURIComponent(slug)}`)).data;
  },
};

export default gridsApi;
